/**
 * Video Chapters Component
 * Provides chapter navigation for interview videos
 */

import type { VideoChapter } from '../types';

// Plyr player interface (loaded via CDN)
interface PlyrPlayer {
  currentTime: number;
  play(): void;
}

export class VideoChapters {
  private video: HTMLVideoElement;
  private container: HTMLElement;
  private chapters: VideoChapter[];
  private player: PlyrPlayer | null = null;
  private currentChapterIndex = -1;
  private chapterElements: NodeListOf<HTMLElement> | null = null;

  constructor(videoElement: HTMLVideoElement, chaptersContainer: HTMLElement, chapters: VideoChapter[]) {
    this.video = videoElement;
    this.container = chaptersContainer;
    this.chapters = chapters;

    if (this.video && this.container && this.chapters.length > 0) {
      this.init();
    }
  }

  private init(): void {
    this.renderChapters();
    this.bindEvents();

    // If using Plyr, get the player instance
    const videoWithPlyr = this.video as HTMLVideoElement & { plyr?: PlyrPlayer };
    if (videoWithPlyr.plyr) {
      this.player = videoWithPlyr.plyr;
    }
  }

  private renderChapters(): void {
    const html = `
      <div class="video-chapters">
        <h3 class="video-chapters__title">Kapitelübersicht</h3>
        <ul class="video-chapters__list" role="list">
          ${this.chapters.map((chapter, index) => `
            <li class="video-chapter"
                data-index="${index}"
                data-time="${chapter.zeit}"
                role="button"
                tabindex="0"
                aria-label="Springe zu ${this.formatTime(chapter.zeit)}: ${chapter.label}">
              <span class="video-chapter__time">${this.formatTime(chapter.zeit)}</span>
              <span class="video-chapter__label">${chapter.label}</span>
              <svg class="video-chapter__play-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </li>
          `).join('')}
        </ul>
      </div>
    `;

    this.container.innerHTML = html;
    this.chapterElements = this.container.querySelectorAll('.video-chapter');
  }

  private bindEvents(): void {
    if (!this.chapterElements) return;

    this.chapterElements.forEach(chapter => {
      chapter.addEventListener('click', () => {
        const time = parseFloat(chapter.dataset.time || '0');
        this.seekTo(time);
      });

      chapter.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const time = parseFloat(chapter.dataset.time || '0');
          this.seekTo(time);
        }
      });
    });

    this.video.addEventListener('timeupdate', () => {
      this.updateActiveChapter();
    });

    this.video.addEventListener('plyr:ready', ((event: CustomEvent<{ plyr: PlyrPlayer }>) => {
      this.player = event.detail.plyr;
    }) as EventListener);
  }

  private seekTo(time: number): void {
    if (this.player) {
      this.player.currentTime = time;
      this.player.play();
    } else if (this.video) {
      this.video.currentTime = time;
      this.video.play();
    }
  }

  private updateActiveChapter(): void {
    const currentTime = this.player ? this.player.currentTime : this.video.currentTime;

    let newChapterIndex = -1;
    for (let i = this.chapters.length - 1; i >= 0; i--) {
      if (currentTime >= this.chapters[i].zeit) {
        newChapterIndex = i;
        break;
      }
    }

    if (newChapterIndex !== this.currentChapterIndex && this.chapterElements) {
      this.currentChapterIndex = newChapterIndex;

      this.chapterElements.forEach((chapter, index) => {
        if (index === this.currentChapterIndex) {
          chapter.classList.add('is-active');
          chapter.setAttribute('aria-current', 'true');
        } else {
          chapter.classList.remove('is-active');
          chapter.removeAttribute('aria-current');
        }
      });

      if (this.currentChapterIndex >= 0) {
        const activeChapter = this.chapterElements[this.currentChapterIndex];
        activeChapter.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }

  private formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  public destroy(): void {
    this.container.innerHTML = '';
    this.chapterElements = null;
  }
}

/**
 * Factory function for easy initialization
 */
export function initVideoChapters(
  videoSelector: string,
  containerSelector: string,
  chapters: VideoChapter[]
): VideoChapters | null {
  const video = document.querySelector<HTMLVideoElement>(videoSelector);
  const container = document.querySelector<HTMLElement>(containerSelector);

  if (video && container) {
    return new VideoChapters(video, container, chapters);
  }
  return null;
}
